/**
 * Classification Configuration Management
 * 
 * This module provides configuration management for the prompt classification system,
 * including feature toggles, service settings, and integration parameters.
 */

import { getEnv, isDevelopment } from './secure-env';

/**
 * Classification service configuration interface
 */
export interface ClassificationConfig {
  /** Whether classification is enabled */
  enabled: boolean;
  /** Whether to run classification synchronously or asynchronously */
  asyncMode: boolean;
  /** Maximum time to wait for sync classification (ms) */
  syncTimeout: number;
  /** Whether to store classification results in database */
  storeResults: boolean;
  /** Whether to log classification operations for debugging */
  enableLogging: boolean;
  /** Whether to enable performance monitoring */
  enableMetrics: boolean;
  /** Default category to use when classification fails */
  defaultCategory: string;
  /** Whether to enable graceful degradation strategies */
  enableFallback: boolean;
}

/**
 * Get default classification configuration
 * This function is called when needed to ensure proper mocking in tests
 */
function getDefaultConfig(): ClassificationConfig {
  return {
    enabled: false, // Disabled by default for safety
    asyncMode: true, // Use async by default to avoid blocking UI
    syncTimeout: 1000, // 1 second timeout for sync operations
    storeResults: true, // Store results for analytics
    enableLogging: Boolean(isDevelopment()), // Enable logging in development (ensure boolean)
    enableMetrics: true, // Enable metrics for performance monitoring
    defaultCategory: 'general', // Safe default category
    enableFallback: true, // Enable graceful degradation
  };
}

/**
 * Load classification configuration from environment variables
 */
export function loadClassificationConfig(): ClassificationConfig {
  const defaultConfig = getDefaultConfig();
  
  return {
    enabled: getEnv('VITE_ENABLE_CLASSIFICATION', { 
      required: false, 
      fallback: defaultConfig.enabled.toString() 
    }).toLowerCase() === 'true',
    
    asyncMode: getEnv('VITE_CLASSIFICATION_ASYNC_MODE', { 
      required: false, 
      fallback: defaultConfig.asyncMode.toString() 
    }).toLowerCase() === 'true',
    
    syncTimeout: parseInt(getEnv('VITE_CLASSIFICATION_SYNC_TIMEOUT', { 
      required: false, 
      fallback: defaultConfig.syncTimeout.toString() 
    }), 10),
    
    storeResults: getEnv('VITE_CLASSIFICATION_STORE_RESULTS', { 
      required: false, 
      fallback: defaultConfig.storeResults.toString() 
    }).toLowerCase() === 'true',
    
    enableLogging: getEnv('VITE_CLASSIFICATION_ENABLE_LOGGING', { 
      required: false, 
      fallback: defaultConfig.enableLogging.toString() 
    }).toLowerCase() === 'true',
    
    enableMetrics: getEnv('VITE_CLASSIFICATION_ENABLE_METRICS', { 
      required: false, 
      fallback: defaultConfig.enableMetrics.toString() 
    }).toLowerCase() === 'true',
    
    defaultCategory: getEnv('VITE_CLASSIFICATION_DEFAULT_CATEGORY', { 
      required: false, 
      fallback: defaultConfig.defaultCategory 
    }),
    
    enableFallback: getEnv('VITE_CLASSIFICATION_ENABLE_FALLBACK', { 
      required: false, 
      fallback: defaultConfig.enableFallback.toString() 
    }).toLowerCase() === 'true',
  };
}

/**
 * Cached configuration instance
 */
let cachedConfig: ClassificationConfig | null = null;

/**
 * Get current classification configuration (cached)
 */
export function getClassificationConfig(): ClassificationConfig {
  if (!cachedConfig) {
    cachedConfig = loadClassificationConfig();
  }
  return cachedConfig;
}

/**
 * Reset configuration cache (useful for testing or dynamic config updates)
 */
export function resetConfigCache(): void {
  cachedConfig = null;
}

/**
 * Validate classification configuration
 */
export function validateClassificationConfig(config: ClassificationConfig): string[] {
  const errors: string[] = [];
  
  if (config.syncTimeout < 100) {
    errors.push('syncTimeout must be at least 100ms');
  }
  
  if (config.syncTimeout > 10000) {
    errors.push('syncTimeout should not exceed 10 seconds');
  }
  
  if (!config.defaultCategory || config.defaultCategory.trim() === '') {
    errors.push('defaultCategory cannot be empty');
  }
  
  return errors;
}

/**
 * Check if classification is available and properly configured
 */
export function isClassificationAvailable(): boolean {
  try {
    const config = getClassificationConfig();
    const errors = validateClassificationConfig(config);
    
    // Return true only if enabled and no validation errors
    return config.enabled && errors.length === 0;
  } catch (error) {
    // If config loading fails, classification is not available
    return false;
  }
}

/**
 * Get configuration summary for logging/debugging
 */
export function getConfigSummary(): Record<string, any> {
  try {
    const config = getClassificationConfig();
    return {
      enabled: config.enabled,
      asyncMode: config.asyncMode,
      syncTimeout: config.syncTimeout,
      storeResults: config.storeResults,
      enableLogging: config.enableLogging,
      enableMetrics: config.enableMetrics,
      defaultCategory: config.defaultCategory,
      enableFallback: config.enableFallback,
      isAvailable: isClassificationAvailable(),
    };
  } catch (error) {
    return {
      error: 'Failed to load configuration',
      details: error instanceof Error ? error.message : 'Unknown error',
    };
  }
} 